import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Grid2, Paper, Alert, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Header from './widgets/Header';
import Loading from './widgets/Loading';

function IPChecker() {
    const { t } = useTranslation();
    const [ip, setIp] = useState('');
    const [ipData, setIpData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchIPData = async () => {
        const init = atob('aXBiX2xpdmVf');
        const access = atob('Ynh2MG5KR3dvc0RUekdUczMxQmJxNGlXNHJhOTRX');
        const token = atob('OGZMZmplQUJMRA==');
        const url = `https://api.ipbase.com/v2/info?apikey=${init}${access}${token}&ip=${ip}`;
        setError(null);
        setIsLoading(true);
        setIpData(null);
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();

            const transformedData = {
                ip: { value: data.data.ip },
                organization: { value: data.data.connection.organization },
                isp: { value: data.data.connection.isp },
                city: { value: data.data.location.city?.name || 'N/A' },
                region: { value: data.data.location.region?.name || 'N/A' },
                regionCode: { value: data.data.location.region?.alpha2 || 'N/A' },
                country: { value: data.data.location.country?.name || 'N/A' },
                countryCode: { value: data.data.location.country?.alpha2 || 'N/A' },
                continent: { value: data.data.location.continent?.name || 'N/A' },
                location: { value: `${data.data.location.latitude},${data.data.location.longitude}` },
                timezone: { value: data.data.timezone?.id || 'N/A' },
                datacenter: { value: data.data.security.is_datacenter ? t('yes') : t('no'), isDanger: data.data.security.is_datacenter },
                vpn: { value: data.data.security.is_vpn ? t('yes') : t('no'), isDanger: data.data.security.is_vpn },
                anonymous: { value: data.data.security.is_anonymous ? t('yes') : t('no'), isDanger: data.data.security.is_anonymous },
                bot: { value: data.data.security.is_bot ? t('yes') : t('no'), isDanger: data.data.security.is_bot },
                abuser: { value: data.data.security.is_abuser ? t('yes') : t('no'), isDanger: data.data.security.is_abuser },
                attacker: { value: data.data.security.is_known_attacker ? t('yes') : t('no'), isDanger: data.data.security.is_known_attacker },
                proxy: { value: data.data.security.is_proxy ? t('yes') : t('no'), isDanger: data.data.security.is_proxy },
                spam: { value: data.data.security.is_spam ? t('yes') : t('no'), isDanger: data.data.security.is_spam },
                tor: { value: data.data.security.is_tor ? t('yes') : t('no'), isDanger: data.data.security.is_tor },
                icloud_relay: { value: data.data.security.is_icloud_relay ? t('yes') : t('no'), isDanger: data.data.security.is_icloud_relay },
            };

            setIpData(transformedData);
        } catch (error) {
            setError(t('ipChecker.error')); // "Erro ao buscar dados do IP. Tente novamente mais tarde."
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box>
            <Header text={t('ipChecker.title')} />
            <Box sx={{ padding: 2, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ textAlign: 'center', paddingBottom: 2 }}>
                    {t('ipChecker.prompt')}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <TextField
                        value={ip}
                        onChange={(e) => setIp(e.target.value)}
                        placeholder={t('ipChecker.placeholder')}
                        variant="outlined"
                        size="small"
                        sx={{ marginRight: 2, width: { xs: '70%', sm: '50%', md: '30%' } }}
                    />
                    <Button variant="contained" color="primary" onClick={fetchIPData}>
                        {t('ipChecker.details')}
                    </Button>
                </Box>
                {ipData && (
                    <Grid2 container spacing={isMobile ? 1 : 2} justifyContent="center" sx={{ marginTop: 2 }}>
                        {Object.entries(ipData).map(([key, object], index) => (
                            <Grid2
                                item
                                xs={12} sm={6} md={4}
                                key={index}
                                minWidth={isMobile ? '100%' : 300}
                                maxWidth={300}
                            >
                                <Paper
                                    sx={{
                                        padding: 2,
                                        textAlign: 'center',
                                        backgroundColor: key === 'ip' ? '#1976d2' : object.isDanger ? '#FF3131' : '#fff',
                                        color: key === 'ip' ? '#fff' : object.isDanger ? '#fff' : '#000',
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {t(`ipChecker.fields.${key}`)}
                                    </Typography>
                                    <Typography variant="body1">{object.value}</Typography>
                                </Paper>
                            </Grid2>
                        ))}
                    </Grid2>
                )}
                {error && (
                    <Alert severity="error" sx={{ marginBottom: 2, width: { xs: '100%', sm: '70%', md: '40%' }, margin: '0 auto', marginTop: 2 }}>
                        {error}
                    </Alert>
                )}
            </Box>
            <Loading text={t('ipChecker.loading')} isLoading={isLoading} />
        </Box>
    );
}

export default IPChecker;